import React, { useState, useEffect, useRef } from 'react';
import { usePlatformStore } from '../stores/platformStore';
import { RoleFilter } from '../hooks/useChatSearch';

interface ChatSearchBarProps {
    isOpen: boolean;
    onClose: () => void;
    onSearch: (query: string) => void;
    onNavigate: (index: number) => void;
    totalMatches: number;
    currentMatchIndex: number;
    onClear?: () => void;
    roleFilter?: RoleFilter;
    onRoleFilterChange?: (filter: RoleFilter) => void;
}

/**
 * ChatSearchBar - In-chat text search with keyboard navigation and role filtering
 * 
 * Features:
 * - Ctrl+F (Windows/Linux) or Cmd+F (macOS) to open, Escape to close
 * - Real-time search with match counter
 * - Navigate results with arrow buttons or Enter/Shift+Enter
 * - Highlights current match in chat messages
 * - Filter by message role (all/user/assistant/system)
 */
export function ChatSearchBar({
    isOpen,
    onClose,
    onSearch,
    onNavigate,
    totalMatches,
    currentMatchIndex,
    onClear,
    roleFilter = 'all',
    onRoleFilterChange,
}: ChatSearchBarProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    // Use platform store for proper OS detection
    const platform = usePlatformStore((state: { platform: string }) => state.platform);
    const isMac = platform === 'darwin';

    // Auto-focus input when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isOpen]);

    // Add global keyboard listener for opening search
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            // Check for search shortcut (Ctrl/Cmd + F)
            const isModifierPressed = isMac ? e.metaKey : e.ctrlKey;
            
            if (isModifierPressed && e.key === 'f' && !isOpen) {
                e.preventDefault();
            }
        };

        // Only add listener if search is closed
        if (!isOpen) {
            window.addEventListener('keydown', handleGlobalKeyDown);
            return () => window.removeEventListener('keydown', handleGlobalKeyDown);
        }
    }, [isOpen, isMac]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowRoleDropdown(false);
            }
        };

        if (showRoleDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showRoleDropdown]);

    // Handle search input changes with debouncing indicator
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        onSearch(query.trim());
    };

    // Clear search input
    const handleClearInput = () => {
        setSearchQuery('');
        onSearch('');
        inputRef.current?.focus();
    };

    // Handle close button - clear search and close
    const handleClose = () => {
        if (onClear) {
            onClear();
        }
        setSearchQuery('');
        onClose();
    };

    // Navigate to previous match
    const handlePrevious = () => {
        if (totalMatches > 0) {
            const newIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : totalMatches - 1;
            onNavigate(newIndex);
        }
    };

    // Navigate to next match
    const handleNext = () => {
        if (totalMatches > 0) {
            const newIndex = currentMatchIndex < totalMatches - 1 ? currentMatchIndex + 1 : 0;
            onNavigate(newIndex);
        }
    };

    // Keyboard shortcuts
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            handleClose();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) {
                handlePrevious();
            } else {
                handleNext();
            }
        } else if (e.key === 'F3') {
            // F3 for next match
            e.preventDefault();
            if (e.shiftKey) {
                handlePrevious();
            } else {
                handleNext();
            }
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
            // Ctrl/Cmd + G for next match 
            e.preventDefault();
            if (e.shiftKey) {
                handlePrevious();
            } else {
                handleNext();
            }
        }
    };

    // Get display name for role filter
    const getRoleFilterLabel = (filter: RoleFilter): string => {
        switch (filter) {
            case 'all': return 'All';
            case 'user': return 'User';
            case 'assistant': return 'Assistant';
            default: return 'All';
        }
    };

    // Get icon for role filter
    const getRoleFilterIcon = (filter: RoleFilter): string => {
        switch (filter) {
            case 'all': return 'fa-users';
            case 'user': return 'fa-user';
            case 'assistant': return 'fa-robot';
            default: return 'fa-users';
        }
    };

    const handleRoleFilterSelect = (filter: RoleFilter) => {
        if (onRoleFilterChange) {
            onRoleFilterChange(filter);
        }
        setShowRoleDropdown(false);
    };

    if (!isOpen) return null;

    const roleFilterOptions: RoleFilter[] = ['all', 'user', 'assistant'];

    return (
        <div 
            className="flex items-center gap-2 bg-neutral-600 border border-neutral-500 rounded-lg px-3 py-2 shadow-lg max-w-full"
            style={{ WebkitAppRegion: 'no-drag' } as any}
            role="search"
            aria-label="Chat search"
        >
            {/* Search Input */}
            <div className="relative flex items-center min-w-0 flex-shrink">
                <i className="fas fa-search text-neutral-300 text-sm absolute left-2 pointer-events-none" />
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Find in chat..."
                    className="bg-neutral-700 text-neutral-100 text-sm border border-neutral-500 rounded px-3 pl-8 pr-8 py-1.5 w-full min-w-[120px] max-w-[240px] focus:outline-none focus:border-red-500 placeholder-neutral-400"
                    aria-label="Search query"
                    aria-describedby="search-results-info"
                />
                {/* Clear button inside input */}
                {searchQuery && (
                    <button
                        onClick={handleClearInput}
                        className="absolute right-2 w-4 h-4 flex items-center justify-center text-neutral-400 hover:text-neutral-100 transition-colors duration-200"
                        title="Clear search"
                        aria-label="Clear search"
                        type="button"
                    >
                        <i className="fas fa-times text-xs" />
                    </button>
                )}
               
            </div>
            {/* Role Filter Dropdown */}
            {onRoleFilterChange && (
                <div className="relative flex-shrink-0" ref={dropdownRef}>
                    <button
                        onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                        className="flex items-center gap-1.5 bg-neutral-500 border border-neutral-400 rounded px-2 py-1.5 hover:bg-neutral-400 transition-colors duration-200 text-neutral-100 text-sm whitespace-nowrap"
                        title="Filter by role"
                        aria-label="Filter by message role"
                        type="button"
                    >
                        <i className={`fas ${getRoleFilterIcon(roleFilter)} text-xs`} />
                        <span>{getRoleFilterLabel(roleFilter)}</span>
                        <i className={`fas fa-chevron-${showRoleDropdown ? 'up' : 'down'} text-xs`} />
                    </button>
                    
                    {showRoleDropdown && (
                        <div className="absolute top-full left-0 mt-1 bg-neutral-600 border border-neutral-500 rounded shadow-lg z-50 min-w-[120px]">
                            {roleFilterOptions.map((filter) => (
                                <button
                                    key={filter}
                                    onClick={() => handleRoleFilterSelect(filter)}
                                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-neutral-500 transition-colors duration-200 first:rounded-t last:rounded-b ${
                                        roleFilter === filter ? 'bg-neutral-500 text-red-400' : 'text-neutral-100'
                                    }`}
                                    type="button"
                                >
                                    <i className={`fas ${getRoleFilterIcon(filter)} text-xs w-4`} />
                                    <span>{getRoleFilterLabel(filter)}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}


            <div 
                id="search-results-info"
                className="text-neutral-200 text-md whitespace-nowrap min-w-[60px] text-center flex-shrink-0"
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                {searchQuery ? (
                    totalMatches > 0 ? (
                        `${currentMatchIndex + 1} / ${totalMatches}`
                    ) : (
                        'No results'
                    )
                ) : (
                    <span className="text-neutral-200">0 / 0</span>
                )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-1 flex-shrink-0">
                <button
                    onClick={handlePrevious}
                    disabled={totalMatches === 0}
                    className="w-7 h-7 flex items-center justify-center bg-neutral-500 border-0 cursor-pointer rounded hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                    title="Previous match"
                    aria-label={`Previous match${totalMatches > 0 ? `, ${currentMatchIndex + 1} of ${totalMatches}` : ''}`}
                    type="button"
                >
                    <i className="fas fa-chevron-up text-neutral-100 text-xs" />
                </button>
                <button
                    onClick={handleNext}
                    disabled={totalMatches === 0}
                    className="w-7 h-7 flex items-center justify-center bg-neutral-500 border-0 cursor-pointer rounded hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                    title="Next match"
                    aria-label={`Next match${totalMatches > 0 ? `, ${currentMatchIndex + 1} of ${totalMatches}` : ''}`}
                    type="button"
                >
                    <i className="fas fa-chevron-down text-neutral-100 text-xs" />
                </button>
            </div>

            {/* Close Button */}
            <button
                onClick={handleClose}
                className="w-7 h-7 flex items-center justify-center bg-neutral-500 border-0 cursor-pointer rounded hover:bg-red-600 transition-colors duration-200 flex-shrink-0"
                title="Close"
                aria-label="Close search"
                type="button"
            >
                <i className="fas fa-times text-neutral-100 text-sm" />
            </button>
        </div>
    );
};

